import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { Box, Grid, Typography, CircularProgress } from '@material-ui/core'
import BreadcrumbsComponent from 'src/components/BreadcrumbsComponent/BreadcrumbsComponent'
import { PUBLISHED_STATUS } from 'src/components/NewsModule/constants'
import InfoCard from 'src/components/InforCard/InfoCard'
import SearchInput from 'src/components/SearchInput/SearchInput'
import EmptyState from 'src/components/EmptyState/EmptyState'
import { getNotices } from 'src/services/api/WebApi/Notices/notices.service'
import { Notice } from 'src/services/api/WebApi/Notices/notices.types'
import { getNoticeCategory } from 'src/components/NoticesContent/constants'
import { useGlobalStyles } from 'src/mui/styles/globalStyles'
import { useStyles } from './OpportunitiesSearchContent.styles'

const OpportunitiesSearchContent = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const classes = useStyles()
  const globalClasses = useGlobalStyles()
  const query = useMemo(() => {
    const searchParam = new URLSearchParams(location.search).get('q') || ''
    return decodeURIComponent(searchParam).trim()
  }, [location.search])
  const [notices, setNotices] = useState<Notice[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!query) {
      setNotices([])
      setIsLoading(false)
      return
    }
    const fetchSearchResults = async () => {
      try {
        setIsLoading(true)
        const data = await getNotices()
        const filteredNotices = data.filter((item) => {
          const noticeTitle = item.title?.toLowerCase().trim() || ''
          const searchTerm = query.toLowerCase().trim()
          return (
            noticeTitle.includes(searchTerm) && item.status === PUBLISHED_STATUS
          )
        })
        setNotices(filteredNotices)
      } catch (error) {
        console.error('Erro ao buscar editais:', error)
      } finally {
        setIsLoading(false)
      }
    }
    fetchSearchResults()
  }, [query])

  const getNoticeType = (notice: Notice) => {
    const title = notice.title?.trim().toLowerCase() || ''
    if (title.includes('clt')) return 'clt'
    if (title.includes('estágio') || title.includes('estagio')) {
      return 'estágio'
    }
    if (title.includes('bolsa')) return 'bolsa'
    return notice.type || notice.category || notice.categoryName || 'vaga'
  }
  return (
    <Box className={globalClasses.containerPages}>
      <Box className={globalClasses.topPageBreadcrumbsSearch}>
        <BreadcrumbsComponent
          crumbs={[
            {
              label: t('navbar.start'),
              path: '/'
            },
            {
              label: t('navbar.opportunities'),
              path: '/opportunities'
            },
            {
              label: t('notices.result title')
            }
          ]}
        />
        <SearchInput
          redirectUrl="/opportunities/search"
          placeholder={t('notices.search-placeholder')}
          initialValue={query}
        />
      </Box>
      <Box className={classes.root}>
        <Box className={classes.content}>
          <Typography variant="body1" className={classes.title}>
            {isLoading
              ? `${t('notices.load')} ${t('notices.results-for')}: ${query}`
              : `${t('notices.showing')} ${notices.length} ${t(
                  'notices.results-for'
                )}: ${query}`}
          </Typography>
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={10}>
              <CircularProgress />
            </Box>
          ) : notices.length > 0 ? (
            <Grid container spacing={4}>
              {notices.map((notice) => {
                const categoryConfig = getNoticeCategory(getNoticeType(notice))
                return (
                  <Grid item xs={12} sm={6} md={4} key={notice.id}>
                    <InfoCard
                      title={notice.title}
                      category={categoryConfig}
                      actionLabel={t('notices.view-more')}
                      link={`/notices/${notice.id}`}
                    />
                  </Grid>
                )
              })}
            </Grid>
          ) : (
            <Box width="100%">
              <EmptyState searchTerm={query} />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default OpportunitiesSearchContent
